import { enableButtonClickTracking } from '@snowplow/browser-plugin-button-click-tracking';
import { enableFormTracking } from '@snowplow/browser-plugin-form-tracking';
import { enableLinkClickTracking } from '@snowplow/browser-plugin-link-click-tracking';
import { trackSiteSearch } from '@snowplow/browser-plugin-site-tracking';
import { trackPageView, trackSelfDescribingEvent } from '@snowplow/browser-tracker';
export function extractSchemaKey(schema) {
    if (!schema.description) {
        throw new Error('Schema must have a description.');
    }
    const parsedDescription = JSON.parse(schema.description);
    if (!parsedDescription._schema_key) {
        throw new Error('Schema description must contain a _schema_key.');
    }
    return parsedDescription._schema_key;
}
export function getSchemaVersion(schema) {
    const schemaKey = extractSchemaKey(schema);
    const versionRegex = /([^/]+)$/;
    const match = schemaKey.match(versionRegex);
    // If there's no match or the last segment doesn't have any characters after the slash, return empty string
    if (!match || match[1] === '') {
        throw new Error(`Invalid schema key format: ${schemaKey}`);
    }
    return match[1];
}
export function getBigQueryColumnName(schema) {
    if (!schema.description) {
        throw new Error('Schema must have a description.');
    }
    const schemaKey = extractSchemaKey(schema);
    const parsedDescription = JSON.parse(schema.description);
    const type = parsedDescription.meta.schemaType;
    const regex = /:([^/]+)\/([^/]+)(?:\/[^/]+)*\/([^-/]+)(?:-.*)?$/;
    const match = schemaKey.match(regex);
    if (!match) {
        throw new Error(`Invalid schema key format: ${schemaKey}`);
    }
    const vendor = match[1].replace(/\./g, '_');
    const event = match[2];
    const majorVersion = match[3];
    const prefix = type === 'event' ? 'unstruct_event' : 'contexts';
    return `${prefix}_${vendor}_${event}_${majorVersion}`;
}
/**
 * Tracks a self-describing event with strongly typed validation using Zod schemas and logs any runtime zod errors.
 *
 * This function provides a type-safe wrapper around the Snowplow `trackSelfDescribingEvent` function.
 * It ensures that the event and context data conform to their respective schemas before tracking.
 *
 * @param event - The event object containing a Zod schema and data conforming to that schema.
 * @param context - An array of context objects, each with its own Zod schema and matching data.
 * @param safeParse - Optional boolean indicating whether to use Zod's `safeParse` (error-tolerant validation). Defaults to true.
 * @param event - The event object containing a Zod schema and data conforming to that schema.
 * @param context - An array of context objects, each with its own Zod schema and matching data.
 * @param safeParse - Optional boolean indicating whether to use Zod's `safeParse` (error-tolerant validation). Defaults to true.
 *
 * @example
 * trackSelfDescribingEventTypeSafe({
 *   event: {
 *     schema: exampleBasicEventEventSchemaV1,
 *     data: { id: 123, name: 'example event' },
 *   },
 *   context: [
 *     { schema: exampleBasicEntityEntitySchemaV1, data: { id: 123, name: 'example' } },
 *     { schema: exampleAnotherBasicEntityEntitySchemaV1, data: { id: 123, name: 'another-example' } }
 *   ],
 * });
 */
export const trackSelfDescribingEventTypeSafe = ({ event, context, safeParse = true, trackers }) => {
    validateData(event.schema, event.data, 'Event', undefined, safeParse);
    context.forEach((ctx, index) => {
        validateData(ctx.schema, ctx.data, 'Context', index, safeParse);
    });
    const eventSchemaKey = extractSchemaKey(event.schema);
    const contextSchemas = context.map((ctx) => ({
        schema: extractSchemaKey(ctx.schema),
        data: ctx.data
    }));
    trackSelfDescribingEvent({
        event: {
            schema: eventSchemaKey,
            data: event.data
        },
        context: contextSchemas
    }, trackers);
};
export function validateData(schema, data, type, index, safeParse = true) {
    const validationResult = safeParse ? schema.safeParse(data) : schema.parse(data);
    const schemaKey = extractSchemaKey(schema);
    if (!validationResult.success) {
        const contextInfo = index !== undefined ? ` for context at index ${index}` : '';
        console.warn(`${type} ${schemaKey} validation failed${contextInfo}:`, validationResult.error);
        return false;
    }
    return true;
}
export const trackPageViewTypeSafe = ({ trackers, context, safeParse = true }) => {
    context?.forEach((ctx, index) => {
        validateData(ctx.schema, ctx.data, 'Context', index, safeParse);
    });
    trackPageView({
        context: context?.map((ctx) => ({
            schema: extractSchemaKey(ctx.schema),
            data: ctx.data
        }))
    }, trackers);
};
export const enableButtonClickTrackingTypeSafe = (config) => {
    config?.context?.forEach((ctx, index) => {
        validateData(ctx.schema, ctx.data, 'Context', index);
    });
    enableButtonClickTracking({
        context: config?.context?.map((ctx) => ({
            schema: extractSchemaKey(ctx.schema),
            data: ctx.data
        }))
    });
};
export const enableLinkClickTrackingTypeSafe = (config) => {
    config?.context?.forEach((ctx, index) => {
        validateData(ctx.schema, ctx.data, 'Context', index);
    });
    enableLinkClickTracking({
        context: config?.context?.map((ctx) => ({
            schema: extractSchemaKey(ctx.schema),
            data: ctx.data
        })),
        trackContent: config?.trackContent
    });
};
/**
 * Helper function to create wrapper for dynamic context functions
 */
function createDynamicContextWrapper(contextFunction, index) {
    return (...args) => {
        const contextParams = parseFormEventParams(args);
        if (!contextParams) {
            return null;
        }
        const contextResult = contextFunction(contextParams);
        if (contextResult === null || !contextResult) {
            return null;
        }
        // Validate the function result at event time
        const isValid = validateData(contextResult.schema, contextResult.data, 'Dynamic Context', index);
        if (!isValid) {
            return null;
        }
        return {
            schema: extractSchemaKey(contextResult.schema),
            data: contextResult.data
        };
    };
}
/**
 * Parse Snowplow form event parameters into typed objects
 */
function parseFormEventParams(args) {
    if (args.length === 3) {
        // Form change/focus events: (element, eventType, value)
        return {
            element: args[0],
            eventType: args[1],
            value: args[2]
        };
    }
    else if (args.length === 2) {
        // Form submission events: (element, innerElements)
        return {
            element: args[0],
            innerElements: args[1]
        };
    }
    console.warn('Unexpected form context function parameters:', args);
    return null;
}
/**
 * Type guard to check if context item is a function
 */
function isDynamicContext(ctx) {
    return typeof ctx === 'function';
}
/**
 * Enable form tracking with type-safe context validation.
 * Supports both static context objects and dynamic context functions.
 *
 * @param config - Configuration object with optional context array
 * @param config.context - Array of context items (static objects or dynamic functions)
 *
 * @example
 * // Static context
 * enableFormTrackingTypeSafe({
 *   context: [{ schema: mySchema, data: { userId: 123 } }]
 * });
 *
 * @example
 * // Dynamic context function
 * enableFormTrackingTypeSafe({
 *   context: [
 *     ({ element, eventType, value }) => ({
 *       schema: myDynamicSchema,
 *       data: { elementId: element.id, eventType, value }
 *     })
 *   ]
 * });
 */
export const enableFormTrackingTypeSafe = (config) => {
    if (!config?.context) {
        // Extract valid FormTrackingConfiguration properties when no context
        const { context: _, ...formTrackingConfig } = config ?? {};
        enableFormTracking(formTrackingConfig);
        return;
    }
    // Validate static context immediately
    config.context.forEach((ctx, index) => {
        if (!isDynamicContext(ctx)) {
            validateData(ctx.schema, ctx.data, 'Context', index);
        }
    });
    const processedContext = config.context.map((ctx, index) => {
        if (isDynamicContext(ctx)) {
            return createDynamicContextWrapper(ctx, index);
        }
        // Static context object
        return {
            schema: extractSchemaKey(ctx.schema),
            data: ctx.data
        };
    });
    // Extract valid FormTrackingConfiguration properties
    const { context: _, ...formTrackingConfig } = config;
    enableFormTracking({
        ...formTrackingConfig,
        context: processedContext
    });
};
/**
 * Tracks a site search event with the provided terms and optional filters and metrics.
 *
 * @param params Configuration for the site search tracking
 * @param params.terms Search terms entered by the user as an array
 * @param params.filters Optional filters applied to the search (e.g., categories)
 * @param params.totalResults Optional number of search results found
 * @param params.pageResults Optional number of search results displayed on the page
 * @param params.context Optional context entities to attach to the event
 * @param params.trackers Optional array of tracker identifiers
 */
export const trackSiteSearchTypeSafe = ({ terms, filters, totalResults, pageResults, context, trackers }) => {
    context?.forEach((ctx, index) => {
        validateData(ctx.schema, ctx.data, 'Context', index);
    });
    trackSiteSearch({
        terms,
        filters,
        totalResults,
        pageResults,
        context: context?.map((ctx) => ({
            schema: extractSchemaKey(ctx.schema),
            data: ctx.data
        }))
    }, trackers);
};
//# sourceMappingURL=data:application/json;base64,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